import { createAsyncThunk } from '@reduxjs/toolkit';

import { RootState } from 'store';
import { getUrlWithParams, urls } from 'store/api';
import { apiClient } from 'utils/http';
import { uploadImage, uploadImageArray } from 'utils/upload';

import {
  TInvestInfrastructuresDraftItem,
  IInvestInfrastructuresItem,
  IInvestInfrastructuresItemResponse,
} from './interfaces';

type TListResponse = {
  data: IInvestInfrastructuresItemResponse[];
  total: number;
};

export const loadInvestInfrastructuresList = createAsyncThunk<
  TListResponse,
  {
    name?: string;
    limit?: number;
    offset?: number;
    entityType?: string;
    type?: string;
    sort?: string;
  },
  { state: RootState }
>('investInfrastructures/loadList', async (params, { getState }) => {
  const { limit } = getState().investInfrastructures;
  const { data } = await apiClient.get<TListResponse>(
    urls.api.investInfrastructures.get,
    {
      params: {
        limit,
        ...params,
      },
    }
  );
  return data;
});

export const loadInvestInfrastructuresItem = createAsyncThunk<
  IInvestInfrastructuresItemResponse,
  string
>('investInfrastructures/loadItem', async id => {
  const { data } = await apiClient.get<IInvestInfrastructuresItemResponse>(
    getUrlWithParams(urls.api.investInfrastructures.getOne, {
      id,
    })
  );
  return data;
});

export const saveInvestInfrastructuresItem = createAsyncThunk<
  IInvestInfrastructuresItem,
  IInvestInfrastructuresItem
>('investInfrastructures/saveItem', async ({ id, ...params }) => {
  const image = await uploadImage(params.image);
  const gallery = await uploadImageArray(params.gallery);

  if (id) {
    const { data } = await apiClient.put<IInvestInfrastructuresItem>(
      getUrlWithParams(urls.api.investInfrastructures.put, { id }),
      {
        ...params,
        image,
        gallery,
      }
    );
    return data;
  } else {
    const { data } = await apiClient.post<IInvestInfrastructuresItem>(
      urls.api.investInfrastructures.post,
      {
        ...params,
        image,
        gallery,
      }
    );
    return data;
  }
});

export const saveInvestInfrastructuresItemAsDraft = createAsyncThunk<
  TInvestInfrastructuresDraftItem,
  TInvestInfrastructuresDraftItem
>('investInfrastructures/saveDraftItem', async ({ id, ...requestParams }) => {
  const image = await uploadImage(requestParams.image);
  const gallery = await uploadImageArray(requestParams.gallery);

  if (id) {
    const { data } = await apiClient.put<TInvestInfrastructuresDraftItem>(
      getUrlWithParams(urls.api.investInfrastructures.draft.put, { id }),
      {
        ...requestParams,
        image,
        gallery,
      }
    );
    return data;
  } else {
    const { data } = await apiClient.post<TInvestInfrastructuresDraftItem>(
      urls.api.investInfrastructures.draft.post,
      {
        ...requestParams,
        image,
        gallery,
      }
    );
    return data;
  }
});

export const setUnPublishInvestInfrastructuresItem = createAsyncThunk<
  void,
  string
>('investInfrastructures/unPublish', async id => {
  return await apiClient.patch(
    getUrlWithParams(urls.api.investInfrastructures.unPublish.patch, { id })
  );
});

export const deleteInvestInfrastructuresItem = createAsyncThunk<
  {
    accessToken: string;
    refreshToken: string;
  },
  {
    id;
  }
>('investInfrastructures/deleteItem', async id => {
  const { data } = await apiClient.delete<{
    accessToken: string;
    refreshToken: string;
  }>(getUrlWithParams(urls.api.investInfrastructures.delete, { id }));
  return data;
});
